/**
 * Updated by Abner Sui on 05/24/2019
 * Add file view tab
 * 05/28/2019   Yoyo Fang   add notes tab
 * 05/29/2019   Yoyo Fang   unify variables for tab index; remove setTitle since title is hide on page.
 *                          when jump to notes tab set tab index through subject.
 * 06/18/2019   Yoyo Fang   integrate entity list with smart reducer
 * 06/26/2019   Bowen Li    change livewire name
 * 08/19/2019   Yoyo Fang   add track by function for ng if
 */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ScreenActionTypes } from '../../redux/actions/screen.actions';
import { Store, select } from '@ngrx/store';
import { AppState, smartSelector } from '../../redux';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { ContactListConfig, ContactListAction, ContactListActionType, ContactListFeedbackType } from '../../tamalelibs/components/contact-list.config';
import { DEFAULT_PAGE_SIZE } from '../../widgets/grid/grid.model';
import { BlastEmailConfig, BlastEmailConfigAction, BlastEmailConfigActionTypes } from '../feeds/blastemail/blastemail.model';
import { BlastEmailViewConstants, ContactsViewConstants, FileViewConstants, EntitiesViewConstants } from '../../tamalelibs/models/smart-reducer.model';
import { EntityListConfig, EntityListActionType, EntityListFeedbackType, EntityListAction } from '../../tamalelibs/components/entity-list.config';
import { KeywordSearchConfig } from '../../widgets/keyword-search/keyword-search.component';
import { take } from 'rxjs/operators';
import { FileViewConfig, FileViewConfigActionTypes, FileViewConfigAction } from '../file-view/file-view.model';
import { ResearchScreenConfigs, ResearchScreenConfigAction, ResearchScreenConfigActionType } from '../../components/research-screen/research-screen.model';
import { ResearchRefinebyService } from '../../services/research-refineby.service';
import * as lodash from 'lodash';
import { PrintService } from '../../services/print.service';
import { ResearchHelperService } from '../../services/research-helper.service';

@Component({
    selector: 'tam-global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
    keyword: string;
    tabData: Array<any> = [
        {
            title: 'Notes',
            disabled: false,
            editable: false,
        },
        {
            title: 'Entities',
            disabled: false,
            editable: false,
        },
        {
            title: 'Contacts',
            disabled: false,
            editable: false,
        },
        {
            title: 'Files',
            disabled: false,
            editable: false,
        },
        {
            title: 'Livewire',
            disabled: false,
            editable: false,
        },
    ];
    currentTab = 0;
    notesTabConfig: ResearchScreenConfigs;
    contactsTabConfig: ContactListConfig;
    entitiesTabConfig: EntityListConfig;
    blastEmailConfig: BlastEmailConfig;
    fileViewConfig: FileViewConfig;
    keywordSearchConfig: KeywordSearchConfig;
    private _fromFilter = false;
    private _subscriptions$: Subscription[] = [];
    constructor(
        private _store: Store<AppState>,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _refinebyService: ResearchRefinebyService,
        private _printService: PrintService,
        private _researchHelper: ResearchHelperService,
    ) {
        this._activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
            this.keywordSearchConfig = new KeywordSearchConfig();
            this.keywordSearchConfig.keyword = params['keyword'];
        });
    }

    ngOnInit() {
        // this._setTitle();

        this.initStore();
        this.keyword = this._findKeyword('?keyword=');
        const tempCategory: Array<string> = lodash.cloneDeep(this._refinebyService.DefaultCategory);
        tempCategory.unshift('entities');
        this.notesTabConfig = new ResearchScreenConfigs('Notes-globalSearchPage', true, this.keyword, '', tempCategory, true);
        this.contactsTabConfig = new ContactListConfig(
            smartSelector('globalSearchPage', ContactsViewConstants.ReducerName),
            'prefix',
            'globalSearchPage',
            true
        );
        this.entitiesTabConfig = new EntityListConfig(
            smartSelector('globalSearchPage', EntitiesViewConstants.ReducerName),
            'prefix',
            'globalSearchPage',
            true
        );
        this.blastEmailConfig = new BlastEmailConfig('prefix', 'globalSearchPage', true);
        this.fileViewConfig = new FileViewConfig('prefix', 'globalSearchPage', true);
        this._subscriptions$.push(
            this._router.events.subscribe((event: any) => {
                if (event instanceof NavigationEnd) {
                    this._searchNavigation(event.url);
                }
            }),

            this._store.pipe(
                select('subChromeName')
            ).subscribe(value => this._changeLivewireName(value)),

            this.keywordSearchConfig.events$.subscribe((event) => {
                if (event.type === 'CHANGE') {
                    this._filterChanged(event.payload.state);
                }
            }),

            // Onetime subscription specific for embedded ContactListComponent init
            this.contactsTabConfig.feedbackSubject.subscribe((action) => {
                if (action.type === ContactListFeedbackType.VIEW_READY) {
                    this.contactsTabConfig.actionSubject.next(new ContactListAction(ContactListActionType.RELOAD, {
                        keyword: this.keyword || '',
                        pageSize: DEFAULT_PAGE_SIZE,
                        page: 1,
                        sortBy: 'full-name',
                        sortOrder: 'asc'
                    }));
                }
            }),

            this.entitiesTabConfig.feedbackSubject.subscribe((action) => {
                if (action.type === EntityListFeedbackType.VIEW_READY) {
                    this.entitiesTabConfig.actionSubject.next(new EntityListAction(EntityListActionType.RELOAD, {
                        keyword: this.keyword || '',
                        pageSize: DEFAULT_PAGE_SIZE,
                        page: 1
                    }));
                }
            }),

            this._printService.actionSubject$.subscribe(action => {
                if (this.currentTab === 0) {
                    this.notesTabConfig.actionSubject$.next(action);
                }
            }),

            this._researchHelper.researchActionSubject$.subscribe(action => {
                if (this.currentTab === 0) {
                    this.notesTabConfig.actionSubject$.next(action);
                }
            }),
        );
    }

    ngOnDestroy() {
        this._subscriptions$.forEach(
            (subscription) => {
                if (subscription && !subscription.closed) {
                    subscription.unsubscribe();
                }
            }
        );
        this.destroyStore();
    }

    initStore() {
        const blastEmailStoreAddAction = {
            type: BlastEmailViewConstants.AddActionTypeName
        };
        blastEmailStoreAddAction[BlastEmailViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(blastEmailStoreAddAction);

        const fileViewStoreAddAction = {
            type: FileViewConstants.AddActionTypeName
        };
        fileViewStoreAddAction[FileViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(fileViewStoreAddAction);

        const contactStoreAddAction = {
            type: ContactsViewConstants.AddActionTypeName
        };
        contactStoreAddAction[ContactsViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(contactStoreAddAction);

        const entityStoreAddAction = {
            type: EntitiesViewConstants.AddActionTypeName
        };
        entityStoreAddAction[EntitiesViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(entityStoreAddAction);

    }
    destroyStore() {
        const blastEmailStoreDeleteAction = {
            type: BlastEmailViewConstants.DeleteActionTypeName
        };
        blastEmailStoreDeleteAction[BlastEmailViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(blastEmailStoreDeleteAction);

        const fileViewStoreDeleteAction = {
            type: FileViewConstants.DeleteActionTypeName
        };
        fileViewStoreDeleteAction[FileViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(fileViewStoreDeleteAction);

        const contactStoreDeleteAction = {
            type: ContactsViewConstants.DeleteActionTypeName
        };
        contactStoreDeleteAction[ContactsViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(contactStoreDeleteAction);

        const entityStoreDeleteAction = {
            type: EntitiesViewConstants.DeleteActionTypeName
        };
        entityStoreDeleteAction[EntitiesViewConstants.ActionKeyName] = 'globalSearchPage';
        this._store.dispatch(entityStoreDeleteAction);

    }

    onTabChange(index: number) {
        this.currentTab = index;
        // TODO: Ala every embedded component stop to update the page title on subchrome, remove 'this._setTitle();'
        // this._setTitle();
        // Different tab has different logic
        if (this.currentTab > 2) {
            this._reloadTabContent(this.keyword);
        }
        if (this.currentTab === 0) {
            setTimeout(() => {
                this.notesTabConfig.actionSubject$.next(new ResearchScreenConfigAction(ResearchScreenConfigActionType.FOCUS_ON_ROW, null));
            });
        }
    }

    trackByFn(index, item) {
        return item.title;
    }

    // change livewire name
    private _changeLivewireName(value) {
        if (value) {
            this.tabData[4].title = value;
        }
    }

    private _filterChanged(keyword: string) {
        this._fromFilter = true;
        const extras = { queryParams: {} };
        extras.queryParams['keyword'] = keyword;
        this._router.navigate(['global_search'], extras);
    }

    private _findKeyword(anchor: string, url: string = null): string {
        if (url === null) {
            url = this._router.url;
        }
        const index = url.indexOf(anchor);
        if (index >= 0) {
            return decodeURIComponent(url.substr(index + anchor.length));
        } else {
            return '';
        }
    }

    private _keywordChanged(keyword: string): boolean {
        return keyword !== this.keyword;
    }

    private _prepareTabIndex(): boolean {
        let tabRedirect = false;
        if (!this._fromFilter) {
            tabRedirect = true;
        }
        this._fromFilter = false;
        return tabRedirect;
    }

    private _reloadTabContent(keyword, toCleanup = false) {
        if (this._keywordChanged(keyword)) {
            this.keyword = keyword;
            // since tab 0 is using [hidden] always reload
            this.notesTabConfig.actionSubject$.next(new ResearchScreenConfigAction(
                toCleanup ? ResearchScreenConfigActionType.Reinitialize : ResearchScreenConfigActionType.Filter,
                { searchText: this.keyword }
            ));
        }
        // reapply filter
        switch (this.currentTab) {
            case 1:
                this.entitiesTabConfig.actionSubject.next(new EntityListAction(EntityListActionType.RELOAD, {
                    keyword: this.keyword || '',
                    pageSize: DEFAULT_PAGE_SIZE,
                    page: 1
                }));
                break;
            case 2:
                this.contactsTabConfig.actionSubject.next(new ContactListAction(ContactListActionType.RELOAD, {
                    keyword: this.keyword || '',
                    pageSize: DEFAULT_PAGE_SIZE,
                    page: 1,
                    sortBy: 'full-name',
                    sortOrder: 'asc'
                }));
                break;
            case 3:
                this.fileViewConfig.keyword = this.keyword;
                this.fileViewConfig.actionSubject$.next(new FileViewConfigAction(FileViewConfigActionTypes.FILTER, this.keyword));
                break;
            case 4:
                this.blastEmailConfig.keyword = this.keyword;
                this.blastEmailConfig.actionSubject.next(new BlastEmailConfigAction(BlastEmailConfigActionTypes.FILTER, this.keyword));
                break;
        }
    }

    private _searchNavigation(url: string) {
        const currKeyword = this._findKeyword('?keyword=', url);
        this.keywordSearchConfig.actions$.next({
            type: 'SET',
            payload: currKeyword,
            silentChange: true
        });
        this._reloadTabContent(currKeyword, this._prepareTabIndex());
    }

    private _setTitle() {
        setTimeout(() => {
            this._store.dispatch({
                type: ScreenActionTypes.SET_VALUE,
                payload: { name: 'Search Result' }
            });
        });
    }

}
